// ── Shared semantic status palette ──────────────────────────────────────────
// Single source of truth for "what color means what" across the whole admin.
// Every page-level status map (jobs, invoices, quotes, timesheets, etc.)
// should resolve its domain-specific status string to one of these five
// semantic buckets, then read its Tailwind classes / AntD Tag color from
// here — instead of hand-picking a hex/color per page. That way "green"
// always means success/paid/completed, "red" always means danger/overdue/
// cancelled, etc, everywhere in the app.

export type SemanticStatus = "success" | "warning" | "danger" | "info" | "neutral";

// AntD <Tag color="..."> keywords for each semantic bucket.
export const SEMANTIC_TAG_COLOR: Record<SemanticStatus, string> = {
  success: "success",
  warning: "warning",
  danger: "error",
  info: "processing",
  neutral: "default",
};

// Tailwind utility classes for each semantic bucket (badge/pill styling).
export const SEMANTIC_CLASSES: Record<SemanticStatus, { bg: string; text: string; border: string }> = {
  success: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
  warning: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
  danger: { bg: "bg-red-50", text: "text-red-600", border: "border-red-200" },
  info: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" },
  neutral: { bg: "bg-slate-50", text: "text-slate-500", border: "border-slate-200" },
};

// Hex equivalents of the semantic palette — for chart series, inline
// `style={{ color }}` usages, and other spots that can't take a className.
export const SEMANTIC_HEX: Record<SemanticStatus, string> = {
  success: "#059669", // emerald-600
  warning: "#d97706", // amber-600
  danger: "#dc2626", // red-600
  info: "#013BBE", // brand primary (blue)
  neutral: "#64748b", // slate-500
};

/** `"bg-x text-y"` — for badges that don't need a border. */
export function semanticBadgeClasses(status: SemanticStatus): string {
  const c = SEMANTIC_CLASSES[status];
  return `${c.bg} ${c.text}`;
}

/** `"bg-x text-y border border-z"` — for badges styled with a border. */
export function semanticBorderedClasses(status: SemanticStatus): string {
  const c = SEMANTIC_CLASSES[status];
  return `${c.bg} ${c.text} border ${c.border}`;
}

/**
 * Builds an AntD `<Tag color="...">` lookup table for a domain's status
 * strings from its semantic mapping — e.g. `buildTagColorMap(INVOICE_STATUS_SEMANTIC)`.
 */
export function buildTagColorMap<T extends string>(
  semanticMap: Record<T, SemanticStatus>,
): Record<T, string> {
  return Object.fromEntries(
    Object.entries(semanticMap).map(([status, semantic]) => [status, SEMANTIC_TAG_COLOR[semantic as SemanticStatus]]),
  ) as Record<T, string>;
}

/**
 * Builds a `"bg-x text-y border border-z"` lookup table for a domain's
 * status strings from its semantic mapping.
 */
export function buildBorderedClassMap<T extends string>(
  semanticMap: Record<T, SemanticStatus>,
): Record<T, string> {
  return Object.fromEntries(
    Object.entries(semanticMap).map(([status, semantic]) => [status, semanticBorderedClasses(semantic as SemanticStatus)]),
  ) as Record<T, string>;
}

// ── Domain status → semantic mappings ───────────────────────────────────────
// Add a new domain here (rather than inventing a parallel color map in a page
// file) whenever a new entity gets its own status enum.

export const INVOICE_STATUS_SEMANTIC = {
  draft: "neutral",
  sent: "info",
  partially_paid: "warning",
  paid: "success",
  overdue: "danger",
  written_off: "neutral",
  void: "neutral",
} as const;

export const QUOTE_STATUS_SEMANTIC = {
  draft: "neutral",
  sent: "info",
  viewed: "info",
  accepted: "success",
  rejected: "danger",
  expired: "warning",
  converted: "success",
} as const;

const QUOTE_STATUS_LABEL: Record<keyof typeof QUOTE_STATUS_SEMANTIC, string> = {
  draft: "Draft",
  sent: "Sent",
  viewed: "Viewed",
  accepted: "Accepted",
  rejected: "Rejected",
  expired: "Expired",
  converted: "Converted",
};

// Ready-made `{ cls, label }` map for quote-status pills — the single
// definition both QuotesPage.tsx and QuotesTable.tsx should import instead
// of each hand-rolling their own (previously slightly-different) copy.
export const QUOTE_STATUS_CFG: Record<keyof typeof QUOTE_STATUS_SEMANTIC, { cls: string; label: string }> =
  Object.fromEntries(
    Object.entries(QUOTE_STATUS_SEMANTIC).map(([status, semantic]) => [
      status,
      { cls: semanticBorderedClasses(semantic as SemanticStatus), label: QUOTE_STATUS_LABEL[status as keyof typeof QUOTE_STATUS_SEMANTIC] },
    ]),
  ) as Record<keyof typeof QUOTE_STATUS_SEMANTIC, { cls: string; label: string }>;
